import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import * as clientService from '../services/clientService';
import { parseError } from '../utils/errorMessages';

const ClientContext = createContext(null);

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) throw new Error('useClient must be used within ClientProvider');
  return context;
};

export const ClientProvider = ({ children }) => {
  const { authenticatedFetch } = useAuth();
  const [clients, setClients] = useState([]);
  const [currentClient, setCurrentClient] = useState(null);
  const [activities, setActivities] = useState([]);
  const [activitiesTotal, setActivitiesTotal] = useState(0);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await clientService.getClients(authenticatedFetch);
      setClients(data);
    } catch (err) {
      setError(parseError(err).message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const fetchClientDetail = useCallback(async (clientId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await clientService.getClientDetail(authenticatedFetch, clientId);
      setCurrentClient(data);
      return data;
    } catch (err) {
      setError(parseError(err).message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const createNewClient = useCallback(async (clientData) => {
    setError(null);
    try {
      const data = await clientService.createClient(authenticatedFetch, clientData);
      setClients(prev => [data, ...prev]);
      return data;
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    }
  }, [authenticatedFetch]);

  const updateExistingClient = useCallback(async (clientId, clientData) => {
    setError(null);
    try {
      const data = await clientService.updateClient(authenticatedFetch, clientId, clientData);
      setClients(prev => prev.map(c => c.id === clientId ? { ...c, ...data } : c));
      if (currentClient?.id === clientId) {
        setCurrentClient(prev => ({ ...prev, ...data }));
      }
      return data;
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    }
  }, [authenticatedFetch, currentClient?.id]);

  const deleteExistingClient = useCallback(async (clientId) => {
    setError(null);
    try {
      await clientService.deleteClient(authenticatedFetch, clientId);
      setClients(prev => prev.filter(c => c.id !== clientId));
      if (currentClient?.id === clientId) {
        setCurrentClient(null);
      }
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    }
  }, [authenticatedFetch, currentClient?.id]);

  const fetchActivities = useCallback(async (clientId, limit = 50, offset = 0) => {
    setActivitiesLoading(true);
    try {
      const data = await clientService.getClientActivities(authenticatedFetch, clientId, limit, offset);
      if (offset === 0) {
        setActivities(data.activities);
      } else {
        setActivities(prev => [...prev, ...data.activities]);
      }
      setActivitiesTotal(data.total);
      return data;
    } catch (err) {
      setError(parseError(err).message);
      return null;
    } finally {
      setActivitiesLoading(false);
    }
  }, [authenticatedFetch]);

  const createNewActivity = useCallback(async (clientId, activityData) => {
    try {
      const data = await clientService.createActivity(authenticatedFetch, clientId, activityData);
      setActivities(prev => [data, ...prev]);
      setActivitiesTotal(prev => prev + 1);
      return data;
    } catch (err) {
      setError(parseError(err).message);
      throw err;
    }
  }, [authenticatedFetch]);

  const value = {
    clients,
    currentClient,
    activities,
    activitiesTotal,
    activitiesLoading,
    loading,
    error,
    fetchClients,
    fetchClientDetail,
    createNewClient,
    updateExistingClient,
    deleteExistingClient,
    fetchActivities,
    createNewActivity,
  };

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
};
